import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, ShieldCheck } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Overline } from "../components/Reveal";
import { PageLoader } from "../components/Loader";
import { useContent } from "../lib/useContent";

export default function TravelClubReviews() {
  const { data: clubs, loading } = useContent("/clubs");
  return (
    <>
      <SEO
        title="Travel Club Reviews"
        description="Transparent, consumer-focused reviews of Club Travalia, Ascendant Holidays, Aloha Travelers, and Arrivia — pros, cons, fees, and the bottom line."
        path="/travel-club-reviews"
      />

      {/* HERO */}
      <section className="relative flex min-h-[58vh] items-end overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1713843841925-6af6ed0df472?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt="Infinity pool overlooking the ocean at an exclusive travel club resort"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="hero-scrim absolute inset-0" />
        <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-32 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-4 py-1.5 text-sm font-semibold text-white">
              <ShieldCheck className="h-4 w-4" /> Independent & Unbiased
            </span>
            <h1 className="mt-5 font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Travel Club Reviews
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-slate-200">
              Four clubs. Four full breakdowns. We dig into the fees, the fine print, the
              inventory, and the support so you can decide with eyes wide open.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CLUB LIST */}
      <section className="section-pad mx-auto max-w-7xl space-y-10 px-5 lg:px-8">
        {loading && <PageLoader />}
        {(clubs || []).map((club, i) => (
          <Reveal key={club.slug} delay={0.04}>
            <article
              data-testid={`club-card-${club.slug}`}
              className="grid overflow-hidden rounded-[2rem] card-3d lg:grid-cols-2"
            >
              <div className="relative min-h-[280px] overflow-hidden">
                <img
                  src={club.image}
                  alt={club.alt}
                  className="h-full w-full object-cover"
                />
                <span className="absolute left-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3.5 py-1.5 text-sm font-bold text-navy backdrop-blur">
                  <Star className="h-4 w-4 fill-accent text-accent" />
                  {club.rating.toFixed(1)} / 5
                </span>
              </div>
              <div className="flex flex-col p-8 sm:p-10">
                <Overline>{club.verdict}</Overline>
                <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-navy">
                  {club.name}
                </h2>
                {club.partner && (
                  <span className="mt-2 inline-flex w-fit items-center gap-1.5 rounded-full bg-accent/20 px-3 py-1 text-xs font-bold uppercase tracking-wide text-navy">
                    <Star className="h-3.5 w-3.5 fill-accent text-accent" /> Featured Partner · Sponsored
                  </span>
                )}
                <p className="mt-3 leading-relaxed text-slate-600">{club.summary}</p>

                <div className="mt-6 grid grid-cols-2 gap-4">
                  {club.scores.map((s) => (
                    <div key={s.label}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-navy">{s.label}</span>
                        <span className="font-semibold text-secondary">{s.value}</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-secondary to-primary"
                          style={{ width: `${s.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <Link
                  to={`/travel-club-reviews/${club.slug}`}
                  data-testid={`club-readmore-${club.slug}`}
                  className="group mt-8 inline-flex items-center gap-2 self-start rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-navy/90"
                >
                  Read the full review
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </article>
          </Reveal>
        ))}
      </section>
    </>
  );
}
